import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Eye, Edit, Trash2,
  Grid, List, CheckCircle, XCircle, Package,
  ChevronLeft, ChevronRight, AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  useGetProductsQuery,
  useGetCategoriesQuery,
  useToggleProductActiveMutation,
  useDeleteProductMutation,
} from '../../app/api';

const ProductsList = () => {
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [togglingId, setTogglingId] = useState(null);

  // ✅ RTK Query — automatic caching
  const { data, isLoading } = useGetProductsQuery({
    page,
    limit,
    search: searchTerm || undefined,
    category_id: selectedCategory !== 'All' ? selectedCategory : undefined,
    isActive:
      filterStatus === 'Active'
        ? 'true'
        : filterStatus === 'Inactive'
        ? 'false'
        : undefined,
  });

  const { data: catData } = useGetCategoriesQuery({ limit: 100 });

  const [toggleActive] = useToggleProductActiveMutation();
  const [deleteProduct] = useDeleteProductMutation();

  const products = data?.data?.products || [];
  const categories = catData?.data?.categories || [];
  const pagination = data?.data?.pagination || {};
  const stats = data?.data?.stats || { total: 0, active: 0, inactive: 0, lowStock: 0 };
  const imageBaseUrl = data?.data?.imageBaseUrl || '';

  const getFullImage = (p) => (!p ? '' : p.startsWith('http') ? p : `${imageBaseUrl}${p}`);

  const getProductStatus = (p) => {
    if (!p.isActive) return 'Inactive';
    if (p.stock === 0) return 'Out of Stock';
    if (p.stock <= (p.lowStockAlert || 5)) return 'Low Stock';
    return 'Active';
  };

  const getStatusBadge = (s) =>
    ({
      Active: 'badge-success',
      'Low Stock': 'badge-warning',
      'Out of Stock': 'badge-danger',
      Inactive: 'badge-danger',
    }[s] || 'badge-info');

  const getStatusIcon = (s) =>
    ({
      Active: <CheckCircle size={14} />,
      'Low Stock': <Package size={14} />,
      'Out of Stock': <XCircle size={14} />,
      Inactive: <XCircle size={14} />,
    }[s] || <Package size={14} />);

  const handleToggleActive = async (id, current) => {
    if (!window.confirm(`Change status to ${current ? 'Inactive' : 'Active'}?`)) return;
    setTogglingId(id);
    try {
      await toggleActive(id).unwrap();
      toast.success('Status updated');
      // ✅ Auto refetch due to invalidatesTags
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to toggle status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(id).unwrap();
      toast.success('Product deleted');
      if (products.length === 1 && page > 1) setPage(page - 1);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete product');
    }
  };

  const getPageNumbers = () => {
    const { page: curPage, totalPages } = pagination;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, curPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPage(newPage);
  };

  return (
    <Layout>
      <div className="products-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Products</h1>
            <p className="page-subtitle">Manage your product inventory</p>
          </div>
          <div className="page-actions">
            <button
              className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </button>
            <button
              className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </button>
            <Link to="/products/add" className="btn btn-primary">
              <Plus size={18} />
              Add Product
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="products-stats">
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-primary)' }}>
              <Package size={20} />
            </div>
            <div>
              <span className="stat-label">Total</span>
              <span className="stat-value">{stats.total}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-success)' }}>
              <CheckCircle size={20} />
            </div>
            <div>
              <span className="stat-label">Active</span>
              <span className="stat-value">{stats.active}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-danger)' }}>
              <XCircle size={20} />
            </div>
            <div>
              <span className="stat-label">Inactive</span>
              <span className="stat-value">{stats.inactive}</span>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: 'var(--color-warning)' }}>
              <AlertCircle size={20} />
            </div>
            <div>
              <span className="stat-label">Low Stock</span>
              <span className="stat-value">{stats.lowStock}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="list-toolbar card">
          <div className="list-toolbar-left">
            <div className="search-box">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="search-input"
              />
            </div>
            <select
              className="filter-select"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
            >
              <option value="All">All Categories</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="All">All Status</option>
              <option value="Active">Active ({stats.active})</option>
              <option value="Inactive">Inactive ({stats.inactive})</option>
            </select>
            <select
              className="filter-select"
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setPage(1);
              }}
            >
              <option value="10">10 / page</option>
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
          </div>
          <div className="list-toolbar-right">
            <span className="text-muted">
              Showing {products.length} of {pagination.total}
            </span>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex-center" style={{ height: '300px' }}>
            <div className="spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-content">
                <span className="empty-icon">📦</span>
                <h3>No products found</h3>
              </div>
            </div>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="product-grid">
                {products.map((product) => {
                  const status = getProductStatus(product);
                  return (
                    <div key={product._id} className="product-card">
                      <div className="product-image">
                        {product.image ? (
                          <img
                            src={getFullImage(product.image)}
                            alt={product.name}
                            className="product-thumb"
                          />
                        ) : (
                          '📦'
                        )}
                      </div>
                      <div className="product-body">
                        <h4 className="product-name">{product.name}</h4>
                        <p className="product-category">
                          {product.category?.name || 'Uncategorized'}
                        </p>
                        <p className="product-price">₹{product.price?.toLocaleString()}</p>
                        <button
                          className={`badge ${getStatusBadge(status)} status-toggle-btn`}
                          onClick={() => handleToggleActive(product._id, product.isActive)}
                          disabled={togglingId === product._id}
                        >
                          {togglingId === product._id ? (
                            <span className="spinner-sm" />
                          ) : (
                            getStatusIcon(status)
                          )}
                          {status} ({product.stock})
                        </button>
                      </div>
                      <div className="product-footer">
                        <Link to={`/products/view/${product._id}`} className="btn btn-sm btn-outline">
                          <Eye size={16} />
                        </Link>
                        <Link to={`/products/edit/${product._id}`} className="btn btn-sm btn-primary">
                          <Edit size={16} />
                        </Link>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(product._id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="card">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => {
                        const status = getProductStatus(product);
                        return (
                          <tr key={product._id}>
                            <td>
                              <div className="item-info">
                                <div className="item-avatar">
                                  {product.image ? (
                                    <img
                                      src={getFullImage(product.image)}
                                      alt={product.name}
                                      className="product-thumb-sm"
                                    />
                                  ) : (
                                    '📦'
                                  )}
                                </div>
                                <div>
                                  <div className="item-name">{product.name}</div>
                                </div>
                              </div>
                            </td>
                            <td>{product.category?.name || 'Uncategorized'}</td>
                            <td className="item-price">₹{product.price?.toLocaleString()}</td>
                            <td>{product.stock}</td>
                            <td>
                              <button
                                className={`badge ${getStatusBadge(status)} status-toggle-btn`}
                                onClick={() => handleToggleActive(product._id, product.isActive)}
                                disabled={togglingId === product._id}
                              >
                                {togglingId === product._id ? (
                                  <span className="spinner-sm" />
                                ) : (
                                  getStatusIcon(status)
                                )}
                                {status}
                              </button>
                            </td>
                            <td className="text-right">
                              <div className="action-buttons">
                                <Link
                                  to={`/products/view/${product._id}`}
                                  className="btn btn-sm btn-outline"
                                >
                                  <Eye size={16} />
                                </Link>
                                <Link
                                  to={`/products/edit/${product._id}`}
                                  className="btn btn-sm btn-primary"
                                >
                                  <Edit size={16} />
                                </Link>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(product._id)}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {pagination.total > 0 && (
                  <div className="table-footer">
                    <div className="table-info">
                      Showing{' '}
                      <strong>{(pagination.page - 1) * pagination.limit + 1}</strong> to{' '}
                      <strong>
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </strong>{' '}
                      of <strong>{pagination.total}</strong> products
                    </div>
                    <div className="pagination">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrevPage}
                      >
                        <ChevronLeft size={16} />
                        Prev
                      </button>
                      {getPageNumbers().map((pageNum, index) =>
                        pageNum === '...' ? (
                          <span key={`dots-${index}`} className="pagination-dots">
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            className={`btn btn-sm ${
                              pagination.page === pageNum ? 'btn-primary' : 'btn-outline'
                            }`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        )
                      )}
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNextPage}
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ProductsList;